import { variantProvider } from '@/app'
import { Icon, Text, Touchable, View } from '@/components'
import { useBooleanToggle } from '@codeleap/common'

type TranslationOverlayProps = {
  debugName: string
  /**
   * What the panel says.
   *
   * `null` or `undefined` means there is nothing to show at all and the panel does not
   * render — which is how the visibility rule stays in one place per surface instead of
   * being restated at every call site. An empty string is different on purpose: it means
   * "nothing right now", and keeps the panel mounted so the toggle does not jump around
   * between one subtitle cue and the next.
   */
  text?: string | null
  isRtl?: boolean
  defaultExpanded?: boolean
  /** Defaults to `50svh`. A subtitle cue is a line or two and never reaches it. */
  maxHeight?: string
  /**
   * Placement, owned by the caller — this panel appears in four places and each one sits
   * somewhere different. Applied before the collapsed styles, so a width set here governs
   * the expanded panel and the collapsed one still shrinks to its toggle.
   */
  style?: Record<string, unknown>
}

/**
 * One line of `p1`: `size: 16` at `lineHeight: 1.5` in `app/textStyles.ts`, where the line
 * height is a multiplier rather than the absolute value mobile uses. See `descriptionText`.
 */
const TEXT_LINE_HEIGHT = 24

/**
 * The translucent panel that carries translated text over a lesson image or a video.
 *
 * Presentational only: it is handed a string and renders it. Choosing that string — a page's
 * alt text, or the subtitle cue playing right now — belongs to whoever knows the domain, and
 * keeping it out of here is what lets the same panel serve both without learning what a
 * `Page` or a caption track is.
 */
export const TranslationOverlay = ({
  debugName,
  text,
  isRtl,
  defaultExpanded = true,
  maxHeight = '50svh',
  style
}: TranslationOverlayProps) => {
  const [expanded, toggle] = useBooleanToggle(defaultExpanded)

  if (text === null || text === undefined) return null

  return (
    <View
      style={[
        styles.wrapper,
        style,
        !expanded && styles.wrapperCollapsed,
        !expanded && isRtl && styles.wrapperCollapsedRTL
      ]}
    >
      {expanded && (
        <View style={[styles.content, { maxHeight }, isRtl && styles.contentRTL]}>
          <Text
            text={text}
            variants={['p1', 'color:neutral1']}
            style={[styles.descriptionText, isRtl ? styles.textRTL : undefined]}
          />
        </View>
      )}

      {expanded && <View style={styles.divider} />}

      <Touchable
        debugName={`${debugName} toggle`}
        onPress={toggle}
        style={[styles.footer, isRtl ? styles.footerRTL : styles.footerLTR]}
      >
        <Icon name="translation-icon" variants={['iconSize:3']} debugName={`${debugName} icon`} />
        <Icon
          name="toggle-icon"
          variants={['iconSize:3']}
          debugName={`${debugName} toggle icon`}
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
        />
      </Touchable>
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      ...theme.presets.fullWidth,
      ...theme.presets.column,
      backgroundColor: theme.colors.overlay,
      backdropFilter: 'blur(2px)',
      overflow: 'hidden'
    },
    wrapperCollapsed: {
      width: 'fit-content'
    },
    // Collapsed, the panel is just its toggle, and it sits on the side the reader starts from.
    // `fit-content` alone leaves it at the start of the line, which is right for a
    // left-to-right language only by accident of normal flow.
    wrapperCollapsedRTL: {
      marginInlineStart: 'auto'
    },
    content: {
      ...theme.presets.column,
      ...theme.spacing.padding(2),
      ...theme.spacing.gap(0.5),
      // Pinned LTR so the Arabic branch below flips alignment without reordering the box
      // itself, which would move the scrollbar to the other side mid-lesson.
      direction: 'ltr',
      overflowY: 'auto'
    },
    contentRTL: {
      alignItems: 'flex-end'
    },
    descriptionText: {
      ...theme.spacing.marginTop(1),
      // Multi-line WebVTT cues arrive with their newlines intact, and this is what keeps them.
      whiteSpace: 'pre-line',
      // A floor of one line. Speech has gaps, and the cue is an empty string through every one
      // of them — without this the box shrank to its padding and sprang back a moment later,
      // several times a sentence. Holding one line's worth of space costs an empty strip over
      // the video and buys a panel that stays where the reader left it.
      minHeight: TEXT_LINE_HEIGHT
    },
    textRTL: {
      textAlign: 'right'
    },
    divider: {
      ...theme.presets.fullWidth,
      height: 1,
      backgroundColor: theme.colors.neutral5,
      ...theme.spacing.marginHorizontal(2),
      width: 'auto'
    },
    footer: {
      ...theme.presets.fullWidth,
      ...theme.presets.row,
      ...theme.presets.alignCenter,
      ...theme.spacing.padding(2),
      ...theme.spacing.gap(0.5)
    },
    footerLTR: {
      ...theme.presets.justifyStart,
      flexDirection: 'row'
    },
    footerRTL: {
      ...theme.presets.justifyStart,
      flexDirection: 'row-reverse'
    }
  }),
  true
)
